import React, { useState, useEffect } from 'react';
import { supplyChainService } from '../services/apiClient';
import { ReplenishmentRecommendation as ApiReplenishmentRecommendation } from '../types/api';
import ForecastView from './ForecastView';
import { useForecastQuery } from '../hooks/useSupplyChainQueries';

// Module-level cache to persist data across navigation (Recommendations only)
const replenishmentCache: {
  data: Map<string, { value: any; timestamp: number }>;
  ttl: number;
} = {
  data: new Map(),
  ttl: 20 * 60 * 1000, // 20 minutes TTL
};

function getCachedRepl(key: string): any | null {
  const cached = replenishmentCache.data.get(key);
  if (cached && Date.now() - cached.timestamp < replenishmentCache.ttl) {
    console.log(`Replenishment: Using cached data for ${key}`);
    return cached.value;
  }
  return null;
}

function setCachedRepl(key: string, value: any): void {
  replenishmentCache.data.set(key, { value, timestamp: Date.now() });
  console.log(`Replenishment: Cached data for ${key}`);
}

type ReplenishmentRecommendation = ApiReplenishmentRecommendation;

interface ReplenishmentRecommendationsProps {
  viewMode: 'forecast' | 'recommendations';
}

interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOption = options.find((option) => option.value === value) || options[0];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span>{selectedOption?.label || ''}</span>
        <svg className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${option.value === value
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReplenishmentRecommendations: React.FC<ReplenishmentRecommendationsProps> = ({ viewMode }) => {

  // Filters
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [monthsAhead, setMonthsAhead] = useState(3);

  // Recommendations Data State
  const [allRecommendations, setAllRecommendations] = useState<ReplenishmentRecommendation[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false); // Renamed to distinguish from forecast loading
  const [errorRecs, setErrorRecs] = useState<string | null>(null);

  // Forecast data is only needed for forecast view
  const {
    data: forecastData,
    isLoading: loadingForecast,
    isError: isErrorForecast,
    error: errorForecast,
    refetch: refetchForecast
  } = useForecastQuery(selectedWarehouse, viewMode === 'forecast');

  // Detail modal
  const [selectedDetail, setSelectedDetail] = useState<ReplenishmentRecommendation | null>(null);

  // Fetch Recommendations (Legacy logic)
  useEffect(() => {
    // Only fetch recommendations if we are in that view
    if (viewMode !== 'recommendations') return;

    const cacheKey = `replenishment-all-${monthsAhead}`;
    const cached = getCachedRepl(cacheKey);

    if (cached) {
      setAllRecommendations(cached.recommendations || []);
      setLoadingRecs(false);
      return;
    }

    fetchRecommendations();
  }, [monthsAhead, viewMode]);

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    setErrorRecs(null);
    const cacheKey = `replenishment-all-${monthsAhead}`;

    try {
      // Always fetch ALL warehouses, filter client-side for recommendations
      // Note: The Forecast API handles filtering on server-side if we pass a warehouse 
      // but Recommendations API logic seemed to rely on fetching all and filtering locally in original code.
      // We will keep original logic for recommendations.
      const response = await supplyChainService.getReplenishmentRecommendations(
        'all',
        monthsAhead,
        'all'
      );
      setAllRecommendations(response.recommendations || []);
      setCachedRepl(cacheKey, response);
    } catch (err) {
      console.error('Error fetching replenishment recommendations:', err);
      setErrorRecs('Failed to load replenishment recommendations');
    } finally {
      setLoadingRecs(false);
    }
  };

  // Recommendations Client-side filtering
  const recommendations = React.useMemo(() => {
    let filtered = allRecommendations;

    if (selectedWarehouse !== 'all') {
      filtered = filtered.filter(r => r.warehouse === selectedWarehouse);
    }

    if (selectedPriority !== 'all') {
      const priorityOrder = ['critical', 'high', 'medium', 'low'];
      const minPriorityIndex = priorityOrder.indexOf(selectedPriority);
      filtered = filtered.filter(r => priorityOrder.indexOf(r.priority) <= minPriorityIndex);
    }

    return filtered;
  }, [allRecommendations, selectedWarehouse, selectedPriority]);

  // Summary logic for Recommendations
  const summary = React.useMemo(() => {
    if (recommendations.length === 0) return null;
    return {
      total_recommendations: recommendations.length,
      total_units_to_order: recommendations.reduce((sum, r) => sum + r.recommended_order_quantity, 0),
      total_in_transit: recommendations.reduce((sum, r) => sum + (r.in_transit_quantity ?? 0), 0),
      critical_orders: recommendations.filter(r => r.priority === 'critical').length,
      high_priority_orders: recommendations.filter(r => r.priority === 'high').length,
    };
  }, [recommendations]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="px-8 py-6">
        {/* Global Filter Bar (Warehouse applies to ALL views) */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Warehouse Scope</label>
              <CustomDropdown
                value={selectedWarehouse}
                onChange={setSelectedWarehouse}
                options={[
                  { value: 'all', label: 'All Warehouses' },
                  { value: 'UK', label: 'UK' },
                  { value: 'US', label: 'US' },
                  { value: 'CA', label: 'Canada' },
                  { value: 'AU', label: 'Australia' },
                  { value: 'CN', label: 'China' }
                ]}
              />
            </div>

            {/* Dynamic Filters based on active view */}
            {viewMode === 'recommendations' && (
              <>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Priority Filter</label>
                  <CustomDropdown
                    value={selectedPriority}
                    onChange={setSelectedPriority}
                    options={[
                      { value: 'all', label: 'All Priorities' },
                      { value: 'critical', label: 'Critical Only' },
                      { value: 'high', label: 'High+' },
                      { value: 'medium', label: 'Medium+' }
                    ]}
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Planning Horizon</label>
                  <CustomDropdown
                    value={String(monthsAhead)}
                    onChange={(value) => setMonthsAhead(Number(value))}
                    options={[
                      { value: '1', label: '1 Month' },
                      { value: '3', label: '3 Months' },
                      { value: '6', label: '6 Months' },
                      { value: '12', label: '12 Months' }
                    ]}
                  />
                </div>
                <button
                  onClick={fetchRecommendations}
                  className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 font-medium transition-colors border border-blue-200"
                >
                  Rescan
                </button>
              </>
            )}
          </div>
        </div>

        {/* 1. RECOMMENDATIONS VIEW */}
        {viewMode === 'recommendations' && (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-600">Total Recommendations</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{summary.total_recommendations}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-600">Units to Order</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{summary.total_units_to_order.toLocaleString()}</div>
                  <div className="text-xs text-gray-400">net after in-transit</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-purple-600">In Transit</div>
                  <div className="text-2xl font-bold text-purple-600 mt-1">{summary.total_in_transit.toLocaleString()}</div>
                  <div className="text-xs text-purple-400">already on the way</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-red-600">Critical Orders</div>
                  <div className="text-2xl font-bold text-red-600 mt-1">{summary.critical_orders}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-orange-600">High Priority</div>
                  <div className="text-2xl font-bold text-orange-600 mt-1">{summary.high_priority_orders}</div>
                </div>
              </div>
            )}

            {/* Loading / Error States */}
            {loadingRecs && (
              <div className="flex items-center justify-center py-12 bg-white rounded-lg shadow">
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  <span className="ml-3 text-sm">Analyzing stock levels...</span>
                </div>
              </div>
            )}

            {errorRecs && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mb-6">
                {errorRecs}
              </div>
            )}

            {/* Table */}
            {!loadingRecs && !errorRecs && (
              <>
                {recommendations.length > 0 ? (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-purple-500 uppercase tracking-wider">In Transit</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Order Qty</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Arrival Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transport</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {recommendations.map((rec, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rec.product_name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{rec.warehouse}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(rec.priority)}`}>
                                  {rec.priority.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{rec.current_stock.toLocaleString()}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-600">
                                {(rec.in_transit_quantity ?? 0) > 0 ? (
                                  <span className="font-medium">{rec.in_transit_quantity.toLocaleString()}</span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">{rec.recommended_order_quantity.toLocaleString()}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {formatDate(rec.recommended_order_date)}
                                <div className="text-xs text-gray-400">in {rec.days_until_reorder} days</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(rec.expected_arrival_date)}<div className="text-xs text-gray-400">{rec.lead_time_days} days</div></td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{rec.transport_mode}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  onClick={() => setSelectedDetail(rec)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
                    No replenishment recommendations found for the selected criteria.
                  </div>
                )}
              </>
            )}

            {/* Detail Modal */}
            {selectedDetail && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                  <div className="flex justify-between items-center px-4 py-3 border-b">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${selectedDetail.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          selectedDetail.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            selectedDetail.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                        }`}>{selectedDetail.priority.toUpperCase()}</span>
                      <h2 className="text-base font-bold text-gray-800">{selectedDetail.product_name} ({selectedDetail.warehouse})</h2>
                    </div>
                    <button onClick={() => setSelectedDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>Current Stock: <b>{selectedDetail.current_stock}</b></div>
                      <div>Reorder Point: <b>{selectedDetail.reorder_point}</b></div>
                      <div>Order Quantity: <b>{selectedDetail.recommended_order_quantity}</b></div>
                      <div>Target Date: <b>{formatDate(selectedDetail.recommended_order_date)}</b></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* 2. FORECAST VIEW */}
        {viewMode === 'forecast' && (
          <ForecastView
            data={forecastData ?? null}
            loading={loadingForecast}
            error={errorForecast}
            selectedWarehouse={selectedWarehouse}
            onRefresh={refetchForecast}
          />
        )}

      </div>
    </div>
  );
};

export default ReplenishmentRecommendations;
